/**
 * UpdateModal / MaintenanceModal / SessionStaleModal — Strata branded.
 * iter-156bu.
 *
 * All three are non-dismissable for the user — there's no escape hatch
 * because the underlying state requires action. The platform admin can
 * clear flags from the admin panel.
 *
 * The Strata color palette uses #4575A8 (steel) for primary actions.
 * We use a dark backdrop + frosted card to match the rest of the app.
 */
import { Button } from '@/components/ui/button';
import { RefreshCw, Wrench, LogIn, AlertTriangle } from 'lucide-react';

const Backdrop = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    role="dialog"
    aria-modal="true"
  >
    <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-6 sm:p-8">
      {children}
    </div>
  </div>
);

export const UpdateModal = ({ onRefresh, message }) => (
  <Backdrop testId="update-modal">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-full bg-[#4575A8]/15 flex items-center justify-center">
        <RefreshCw className="w-5 h-5 text-[#4575A8]" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Outfit' }}>
          Strata has been updated
        </h2>
        <p className="text-xs text-muted-foreground">A newer version is ready</p>
      </div>
    </div>
    <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
      {message || 'Please refresh your page to load the latest version of Strata. Your work is saved.'}
    </p>
    <Button
      onClick={onRefresh}
      className="w-full bg-[#4575A8] hover:bg-[#5C8AC2] text-white"
      data-testid="update-modal-refresh-btn"
    >
      <RefreshCw className="w-4 h-4 mr-2" />
      Refresh now
    </Button>
  </Backdrop>
);

export const MaintenanceModal = ({ message }) => (
  <Backdrop testId="maintenance-modal">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-full bg-[#C4A561]/15 flex items-center justify-center">
        <Wrench className="w-5 h-5 text-[#C4A561]" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Outfit' }}>
          Strata is under maintenance
        </h2>
        <p className="text-xs text-muted-foreground">We'll be back shortly</p>
      </div>
    </div>
    <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
      {message || 'Strata is temporarily unavailable while we deploy improvements. Saving and uploads are disabled until we\'re back.'}
    </p>
    <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg p-3">
      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
      <span>You can keep this tab open — it will refresh automatically when service resumes.</span>
    </div>
  </Backdrop>
);

export const SessionStaleModal = ({ onLogin, message }) => (
  <Backdrop testId="session-stale-modal">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-full bg-[#4575A8]/15 flex items-center justify-center">
        <LogIn className="w-5 h-5 text-[#4575A8]" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Outfit' }}>
          Please log back in
        </h2>
        <p className="text-xs text-muted-foreground">Strata was updated</p>
      </div>
    </div>
    <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
      {message || 'Strata was updated and your session needs to refresh. Please log back in to continue.'}
    </p>
    <Button
      onClick={onLogin}
      className="w-full bg-[#4575A8] hover:bg-[#5C8AC2] text-white"
      data-testid="session-stale-login-btn"
    >
      <LogIn className="w-4 h-4 mr-2" />
      Log in again
    </Button>
  </Backdrop>
);

export default UpdateModal;
